import React, { useMemo, useRef, useState } from "react";

type ModernFilePickerProps = {
    label?: string;
    helperRight?: string;
    accept?: string;
    maxSizeMB?: number;
    multiple: boolean;
    onFile?: (file: File | null) => void;
    buttonText?: string;
    className?: string;
};

function formatBytes(bytes: number) {
    const units = ["B", "KB", "MB", "GB"];
    let v = bytes;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function isImage(file?: File | null) {
    return !!file && file.type.startsWith("image/");
}

/**
 * Mikrokomponent: moderne filvelger (uten sort bakgrunn).
 * - Drag & drop
 * - Filnavn + størrelse
 * - Forhåndsvisning for bilder
 */
export default function ModernFilePicker({
    label = "Velg fil",
    helperRight,
    accept = "image/*",
    maxSizeMB = 20,
    multiple,
    onFile,
    buttonText = "Velg fil",
    className = "",
}: ModernFilePickerProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const previewUrl = useMemo(() => {
        if (!files.length) return null;
        const first = files[0];
        if (!first.type.startsWith("image/")) return null;
        return URL.createObjectURL(first);
    }, [files]);

    React.useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const maxBytes = maxSizeMB * 1024 * 1024;

    function validate(f: File) {
        if (f.size > maxBytes) return `Filen er for stor. Maks ${maxSizeMB} MB.`;
        return null;
    }

    function setAndNotify(next: File[]) {
        setFiles(next);
        onFile?.(multiple ? next : next[0] ?? null);
    }

    function handlePicked(list?: FileList | null) {
        setError(null);
        if (!list || !list.length) return;

        const picked = multiple ? Array.from(list) : [list[0]];

        for (const f of picked) {
            const v = validate(f);
            if (v) {
                setError(v);
                return;
            }
        }

        setAndNotify(picked);
    }

    function openDialog() {
        inputRef.current?.click();
    }

    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
        const f = e.target.files?.[0] ?? null;
        handlePicked(f);
        e.target.value = ""; // gjør at samme fil kan velges igjen
    }

    function onDrop(e: React.DragEvent<HTMLDivElement>) {
        e.preventDefault();
        setDragOver(false);
        handlePicked(e.dataTransfer.files);
    }

    return (
        <div className={multiple ? "md:w-[40%] sm:w-full" : "w-full " + className}>
            <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">{label}</div>
                <div className="text-xs text-slate-500">{helperRight ?? `Maks ${maxSizeMB} MB`}</div>
            </div>

            <div
                className={
                    "group relative rounded-2xl border p-4 transition " +
                    (dragOver
                        ? "border-orange-400/70 bg-orange-50"
                        : "border-slate-200 bg-white hover:bg-slate-50")
                }
                onDragEnter={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple={multiple}
                    onChange={(e) => {
                        handlePicked(e.target.files);
                        e.target.value = "";
                    }}
                    className="sr-only"
                />

                <div className="flex items-center gap-4">
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-200">
                        {previewUrl ? (
                            <img src={previewUrl} alt="Forhåndsvisning" className="h-full w-full object-cover" />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center text-slate-500">
                                <svg
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M12 16V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <path
                                        d="M9 11L12 8L15 11"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M20 16.5C21.2 16 22 14.9 22 13.6C22 12 20.8 10.7 19.2 10.6C18.6 7.4 15.8 5 12.4 5C9 5 6.2 7.4 5.6 10.6C4 10.7 2.8 12 2.8 13.6C2.8 14.9 3.6 16 4.8 16.5"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>
                        )}
                    </div>

                    <div className="min-w-0 flex-1">
                        {files.length ? (
                            <>
                                <div className="truncate text-sm font-semibold text-slate-900">
                                    {files.length === 1
                                        ? files[0].name
                                        : `${files.length} filer valgt`}
                                </div>
                                <div className="text-xs text-slate-500">
                                    {files.map(f => formatBytes(f.size)).join(" • ")}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* <div className="text-sm font-semibold text-slate-900">Dra og slipp her</div>
                                <div className="text-xs text-slate-500">… eller velg fra maskinen</div> */}
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {files.length ? (
                            <button
                                type="button"
                                onClick={() => setAndNotify([])}
                                className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold"
                            >
                                Fjern
                            </button>
                        ) : null}

                        <button
                            type="button"
                            onClick={openDialog}
                            className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-semibold text-black shadow-sm hover:bg-orange-400"
                        >
                            {files ? "Velg fil" : buttonText}
                        </button>
                    </div>
                </div>

                {error ? <div className="mt-3 text-xs text-red-600">{error}</div> : null}
                <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-transparent transition group-hover:ring-slate-200" />
            </div>
        </div>
    );
}